import { useState } from 'react';
import { useAuth } from '@/lib/auth';
import { Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Trophy, ArrowLeft } from 'lucide-react';

export default function Auth() {
  const { user, loading, signIn, signUp, resetPasswordForEmail, updatePassword } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [mode, setMode] = useState<'login' | 'signup' | 'forgot' | 'update'>(
    searchParams.get('type') === 'recovery' ? 'update' : 'login'
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (user && mode !== 'update') return <Navigate to="/" replace />;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    let result;
    if (mode === 'login') {
      result = await signIn(email, password);
    } else if (mode === 'signup') {
      result = await signUp(email, password);
    } else if (mode === 'forgot') {
      result = await resetPasswordForEmail(email);
    } else if (mode === 'update') {
      result = await updatePassword(password);
    }

    if (result?.error) {
      toast({ variant: 'destructive', title: 'Error', description: result.error.message });
    } else {
      if (mode === 'signup') {
        toast({ title: 'Account created!', description: 'Check your email to confirm your account.' });
      } else if (mode === 'forgot') {
        toast({ title: 'Email sent!', description: 'Check your email for the password reset link.' });
        setMode('login');
      } else if (mode === 'update') {
        toast({ title: 'Password updated!', description: 'Your password has been changed successfully.' });
        navigate('/');
      }
    }

    setSubmitting(false);
  };

  const getTitle = () => {
    switch (mode) {
      case 'signup': return 'Create a new account';
      case 'forgot': return 'Reset password';
      case 'update': return 'Update password';
      default: return 'Sign in to your account';
    }
  };

  const getButtonText = () => {
    if (submitting) return 'Loading...';
    switch (mode) {
      case 'signup': return 'Sign Up';
      case 'forgot': return 'Send Reset Link';
      case 'update': return 'Update Password';
      default: return 'Sign In';
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-sm border-0 shadow-xl">
        <CardHeader className="text-center space-y-3">
          <div className="mx-auto w-16 h-16 rounded-2xl bg-primary flex items-center justify-center">
            <Trophy className="h-8 w-8 text-primary-foreground" />
          </div>
          <CardTitle className="text-2xl font-bold tracking-tight">SSC Score Tracker</CardTitle>
          <CardDescription>
            {getTitle()}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode !== 'update' && (
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12"
              />
            )}
            {mode !== 'forgot' && (
              <Input
                type="password"
                placeholder={mode === 'update' ? 'New Password' : 'Password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="h-12"
              />
            )}
            <Button type="submit" className="w-full h-12 text-base font-semibold" disabled={submitting}>
              {getButtonText()}
            </Button>
          </form>

          {mode === 'login' && (
            <div className="mt-4 space-y-2">
              <button
                onClick={() => setMode('forgot')}
                className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Forgot password?
              </button>
              <button
                onClick={() => setMode('signup')}
                className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Don't have an account? Sign up
              </button>
            </div>
          )}

          {mode === 'signup' && (
            <button
              onClick={() => setMode('login')}
              className="mt-4 w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Already have an account? Sign in
            </button>
          )}

          {(mode === 'forgot' || mode === 'update') && (
            <button
              onClick={() => setMode('login')}
              className="mt-4 w-full flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to login
            </button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
